<template>
    <GuestLayout>
        <Head title="Log in" />

        <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <div
                v-if="displayedStatus"
                class="mb-4 font-medium text-sm text-green-600"
            >
                {{ displayedStatus }}
            </div>

            <!-- Google SSO Button -->
            <div v-if="ssoEnabled" class="mb-6">
                <a
                    id="google-sso-button"
                    :href="route('auth.google')"
                    class="flex w-full items-center justify-center gap-3 rounded-md bg-white dark:bg-primary-900 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-50 shadow-sm dark:shadow-md dark:shadow-black/20 ring-1 ring-inset ring-gray-300 dark:ring-primary-700 hover:bg-gray-50 dark:hover:bg-primary-800 transition-colors duration-150"
                >
                    <svg
                        class="h-5 w-5"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                            fill="#4285F4"
                        />
                        <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                        />
                        <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                        />
                        <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                        />
                    </svg>
                    Sign in with Google
                </a>
            </div>

            <!-- SSO Enforcement Alert -->
            <div
                v-if="isSsoEnforced"
                id="sso-enforcement-alert"
                class="mb-4 rounded-md bg-blue-50 dark:bg-blue-900/20 pad-card"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg
                            class="h-5 w-5 text-blue-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-blue-700 dark:text-blue-300">
                            Your organisation requires Google SSO. Please use
                            the button above to sign in.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Divider (only when SSO is enabled) -->
            <div v-if="ssoEnabled && !isSsoEnforced" class="relative mb-6">
                <div class="absolute inset-0 flex items-center">
                    <div
                        class="w-full border-t border-gray-300 dark:border-primary-700"
                    ></div>
                </div>
                <div class="relative flex justify-center text-sm">
                    <span
                        class="bg-white dark:bg-primary-950 px-2 text-gray-500 dark:text-gray-400"
                        >Or sign in with email</span
                    >
                </div>
            </div>

            <!-- Email input — always visible so users can correct their address -->
            <div class="mb-6">
                <InputLabel for="email" value="Email address" />
                <div class="mt-2">
                    <TextInput
                        id="email"
                        v-model="form.email"
                        name="email"
                        type="email"
                        class="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-50 dark:bg-primary-900 shadow-sm dark:shadow-md dark:shadow-black/20 ring-1 ring-inset ring-gray-300 dark:ring-primary-700 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:text-gray-50 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        required
                        autofocus
                        autocomplete="username"
                    />
                </div>
                <InputError class="mt-2" :message="form.errors.email" />
            </div>

            <form
                v-if="!isSsoEnforced"
                class="space-y-6"
                action="#"
                method="POST"
                @submit.prevent="submit"
            >
                <div>
                    <div class="flex items-center justify-between">
                        <InputLabel for="password" value="Password" />
                    </div>
                    <div class="mt-2">
                        <TextInput
                            id="password"
                            v-model="form.password"
                            name="password"
                            type="password"
                            class="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-50 dark:bg-primary-900 shadow-sm dark:shadow-md dark:shadow-black/20 ring-1 ring-inset ring-gray-300 dark:ring-primary-700 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                            required
                            autocomplete="current-password"
                        />
                    </div>
                    <InputError class="mt-2" :message="form.errors.password" />
                </div>

                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <Checkbox
                            id="remember"
                            v-model="form.remember"
                            name="remember"
                        />
                        <label
                            for="remember"
                            class="ml-3 block text-sm leading-6 text-gray-900 dark:text-gray-50"
                            >Remember me</label
                        >
                    </div>

                    <div class="text-sm leading-6">
                        <Link
                            v-if="canResetPassword"
                            :href="route('password.request')"
                            class="font-semibold text-primary-600 hover:text-primary-500"
                        >
                            Forgot password?
                        </Link>
                    </div>
                </div>
                <div>
                    <Button
                        type="submit"
                        :tag="'button'"
                        :size="'lg'"
                        class="flex w-full justify-center"
                        dusk="login-submit-button"
                        >Sign in</Button
                    >
                </div>

                <div v-if="page.props.features?.passkeys" class="relative m-6">
                    <div class="absolute inset-0 flex items-center">
                        <div
                            class="w-full border-t border-gray-300 dark:border-primary-700"
                        ></div>
                    </div>
                    <div class="relative flex justify-center text-sm">
                        <span
                            class="bg-white dark:bg-primary-900 px-2 text-gray-500 dark:text-gray-400"
                            >Or continue with</span
                        >
                    </div>
                </div>

                <div v-if="page.props.features?.passkeys">
                    <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        class="!w-full !justify-center"
                        dusk="login-passkey-button"
                        @click="authenticateWithPasskey"
                    >
                        <template #icon_start>
                            <span>🔑</span>
                        </template>
                        Sign in with Passkey
                    </Button>
                </div>
            </form>
        </div>
    </GuestLayout>
</template>
<script setup lang="ts">
import Checkbox from "@/Components/Checkbox.vue";
import GuestLayout from "@/Layouts/GuestLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import Button from "@/Components/Button.vue";
import TextInput from "@/Components/TextInput.vue";
import { Head, Link, useForm, usePage, router } from "@inertiajs/vue3";
import { computed } from "vue";

const page = usePage();

const props = defineProps<{
    canResetPassword?: boolean;
    status?: string;
    ssoEnabled?: boolean;
    ssoEnforcedDomains?: string[];
}>();

const displayedStatus = computed(() =>
    new URLSearchParams(window.location.search).get("session_expired") === "1"
        ? "Session expired. Please log in again."
        : props.status,
);

const form = useForm({
    email: "",
    password: "",
    remember: false,
});

/**
 * Whether the current email belongs to an SSO-enforced domain.
 * Reactively disables/enables the password form as the user types.
 */
const isSsoEnforced = computed(() => {
    if (!props.ssoEnabled || !props.ssoEnforcedDomains?.length) return false;
    const email = form.email.toLowerCase();
    const atIndex = email.lastIndexOf("@");
    if (atIndex === -1) return false;
    return props.ssoEnforcedDomains.includes(email.substring(atIndex + 1));
});

// Form for passkey authentication
const passkeyForm = useForm({
    email: "",
});

const submit = () => {
    form.post(route("login"), {
        onFinish: () => {
            form.reset("password");
        },
    });
};

// Utility functions for converting between ArrayBuffer and Base64
function base64ToArrayBuffer(base64: string) {
    const binaryString = window.atob(
        base64.replace(/-/g, "+").replace(/_/g, "/"),
    );
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window
        .btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
}

const authenticateWithPasskey = async () => {
    try {
        // Set the email in the passkey form
        passkeyForm.email = form.email;

        // Get authentication options from the server
        const response = await fetch(route("passkeys.assertion.options"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(document
                    ?.querySelector('meta[name="csrf-token"]')
                    ?.getAttribute("content")
                    ? {
                          "X-CSRF-TOKEN": document
                              ?.querySelector('meta[name="csrf-token"]')
                              ?.getAttribute("content") as string,
                      }
                    : {}),
            },
            body: JSON.stringify({ email: form.email }),
        });

        const options = await response.json();

        // Convert base64 strings to ArrayBuffer
        const publicKey = {
            ...options,
            challenge: base64ToArrayBuffer(options.challenge),
            allowCredentials: options.allowCredentials?.map(
                (credential: any) => ({
                    ...credential,
                    id: base64ToArrayBuffer(credential.id),
                }),
            ),
        };

        // Get the credential from the authenticator
        const credential = await navigator.credentials.get({
            publicKey,
        });

        if (!credential) {
            throw new Error("Failed to get credential");
        }

        // @ts-expect-error: WebAuthn returns a PublicKeyCredential; TS lib doesn't include concrete fields on the union
        const { id, rawId, response: credentialResponse, type } = credential;

        // Prepare the authentication data
        const authenticationData = {
            id,
            rawId: arrayBufferToBase64(rawId),
            response: {
                clientDataJSON: arrayBufferToBase64(
                    credentialResponse.clientDataJSON,
                ),
                authenticatorData: arrayBufferToBase64(
                    credentialResponse.authenticatorData,
                ),
                signature: arrayBufferToBase64(credentialResponse.signature),
                userHandle: credentialResponse.userHandle
                    ? arrayBufferToBase64(credentialResponse.userHandle)
                    : null,
            },
            type,
        };

        // Authenticate with the server using Inertia router
        router.post(route("passkeys.authenticate"), {
            start_authentication_response: JSON.stringify(authenticationData),
        });
    } catch (error) {
        console.error("Error authenticating with passkey:", error);
        alert(
            "Failed to authenticate with passkey. Please try again or use your password.",
        );
    }
};
</script>
